export type LineItemSeed = {
  quantity: number
  unitPrice: number
  discountPercentage: number
  taxRate: number
}

export const LINE_ITEM_SEEDS: readonly LineItemSeed[] = [
  { quantity: 2, unitPrice: 120, discountPercentage: 10, taxRate: 20 },
  { quantity: 1, unitPrice: 300, discountPercentage: 5, taxRate: 20 },
]

/**
 * Returns the effective value of a numeric field for computation.
 * Hidden fields contribute 0, except quantity which defaults to 1 (multiplier identity).
 */
function effectiveValue(fieldKey: string, rawValue: number, isVisible: boolean): number {
  if (isVisible) return rawValue
  if (fieldKey === 'quantity') return 1
  return 0
}

export type RowTotals = {
  discountAmount: number
  subtotal: number
  taxAmount: number
  total: number
}

export function computeRowTotals(
  seed: LineItemSeed,
  visibleFields: ReadonlySet<string>
): RowTotals {
  const qty = effectiveValue('quantity', seed.quantity, visibleFields.has('quantity'))
  const price = effectiveValue('unit_price', seed.unitPrice, visibleFields.has('unit_price'))
  const discPct = effectiveValue(
    'discount_percentage',
    seed.discountPercentage,
    visibleFields.has('discount_percentage')
  )
  const taxRt = effectiveValue('tax_rate', seed.taxRate, visibleFields.has('tax_rate'))

  const discountAmount = (qty * price * discPct) / 100
  const subtotal = qty * price - discountAmount
  const taxAmount = (subtotal * taxRt) / 100
  const total = subtotal + taxAmount

  return { discountAmount, subtotal, taxAmount, total }
}

export type FooterTotals = {
  net: number
  tax: number
  total: number
}

export function computeFooterTotals(
  seeds: readonly LineItemSeed[],
  visibleFields: ReadonlySet<string>
): FooterTotals {
  let net = 0
  let tax = 0
  for (const seed of seeds) {
    const row = computeRowTotals(seed, visibleFields)
    net += row.subtotal
    tax += row.taxAmount
  }
  return { net, tax, total: net + tax }
}

export function formatCurrency(value: number): string {
  return `${value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`
}

function formatPercentage(value: number): string {
  return `${String(value)}%`
}

/**
 * Returns the formatted display value for a numeric line-item field,
 * or null if the field is a text field (handled by i18n).
 * Input fields show raw seed values; computed fields show calculated results.
 */
export function formatNumericField(
  fieldKey: string,
  seed: LineItemSeed,
  rowTotals: RowTotals
): string | null {
  switch (fieldKey) {
    case 'quantity':
      return String(seed.quantity)
    case 'unit_price':
      return formatCurrency(seed.unitPrice)
    case 'discount_percentage':
      return formatPercentage(seed.discountPercentage)
    case 'discount_amount':
      return formatCurrency(rowTotals.discountAmount)
    case 'subtotal':
      return formatCurrency(rowTotals.subtotal)
    case 'tax_rate':
      return `VAT ${formatPercentage(seed.taxRate)}`
    case 'tax_amount':
      return formatCurrency(rowTotals.taxAmount)
    case 'total':
      return formatCurrency(rowTotals.total)
    default:
      return null
  }
}
